import React from "react";
import { useParams } from "react-router-dom";
import BidoraLogo from "@/components/BidoraLogo";

/**
 * ShareCards — auto-generates ready-to-post marketing graphics that
 * embed real Bidorah app screenshots inside iPhone frames, surrounded
 * by a bold tagline, trust pills, store badges, and the bidorah.com
 * footer. Each variant is a different layout / message angle, picked
 * via the `:slug` URL param (e.g. /share-cards/bid-live).
 *
 * Page is sized for a 1080×1350 (4:5) social post by default, which
 * works for IG feed and TikTok cover thumbnails. Screenshot the
 * `[data-card]` element at that size with playwright/your phone and
 * post directly.
 */

// Real screenshots captured from the running app, saved to /public.
const SHOTS = {
  live:       "/marketing/shot-live.png",
  categories: "/marketing/shot-categories.png",
  landing:    "/marketing/shot-landing.png",
};

// Phone-frame chrome wrapping a screenshot. Renders the iPhone notch,
// rounded corners, side-button silhouette, and ambient drop-shadow so
// the embedded screen reads as a real device.
function PhoneFrame({ src, tilt = 0, scale = 1, className = "" }) {
  return (
    <div
      className={`phone-frame ${className}`}
      style={{
        transform: `rotate(${tilt}deg) scale(${scale})`,
      }}
    >
      <div className="phone-frame-bezel">
        <div className="phone-frame-notch" />
        <img src={src} alt="Bidorah app preview" className="phone-frame-screen" />
      </div>
    </div>
  );
}

// Pill row of trust signals used at the bottom of cards.
function TrustPills({ items }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
      {items.map((label, i) => (
        <span
          key={i}
          className="px-4 py-2 rounded-full bg-white text-zinc-900 text-sm font-bold ring-1 ring-zinc-200 shadow-sm whitespace-nowrap"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function StoreBadge() {
  return (
    <a
      href="https://bidorah.com"
      className="inline-flex items-center gap-3 bg-zinc-950 text-white px-5 py-3 rounded-full shadow-lg hover:bg-zinc-900 transition"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 3l14 9-14 9V3z" fill="#34D399" />
        <path d="M3 3l14 9L7 17 3 3z" fill="#F59E0B" />
        <path d="M3 21l14-9L7 7 3 21z" fill="#EF4444" />
        <path d="M3 3l14 9 4-2L3 3z" fill="#3B82F6" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-widest text-zinc-400">Get it on</span>
        <span className="block text-lg font-black">Google Play</span>
      </span>
    </a>
  );
}

// =====================================================================
// CARD LAYOUTS
// =====================================================================

function CardBidLive() {
  return (
    <div className="share-card bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BidoraLogo size={48} />
          <span className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-bold">Live-auction commerce</span>
        </div>
        <span className="px-4 py-2 rounded-full bg-zinc-950 text-white text-xs font-bold flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> LAUNCHING · COMING SOON
        </span>
      </header>

      <h1 className="mt-12 text-7xl sm:text-8xl font-black tracking-tighter leading-[0.95]">
        Bid <span className="text-zinc-900">live.</span>{" "}
        <span className="text-[#1D78AB]">Win real.</span>
      </h1>
      <p className="mt-6 text-xl text-zinc-600 max-w-2xl leading-snug">
        Verified U.S. sellers. Real-time bidding. Retail and wholesale in strict lanes — win while you watch.
      </p>

      <div className="mt-8 flex items-center gap-6">
        <StoreBadge />
        <span className="text-zinc-500 text-sm">iOS · coming soon</span>
      </div>

      <div className="relative mt-12 h-[680px] flex items-end justify-center">
        <PhoneFrame src={SHOTS.landing}    tilt={-9} scale={0.85} className="absolute left-[18%] bottom-0" />
        <PhoneFrame src={SHOTS.live}       tilt={0}  scale={1.0}  className="absolute z-10" />
        <PhoneFrame src={SHOTS.categories} tilt={9}  scale={0.85} className="absolute right-[18%] bottom-0" />
      </div>

      <footer className="mt-10 flex flex-col items-center gap-5">
        <TrustPills items={["US Photo-ID verified", "Real-time bids", "Strict B2B gate", "Stripe + PayPal"]} />
        <div className="flex items-center justify-between w-full text-xs text-zinc-500">
          <span>© 2026 BIDORAH, INC.<br/>All rights reserved.</span>
          <span className="text-right">
            <span className="block text-xl font-black text-zinc-900">bidorah.com</span>
            <span className="block uppercase tracking-widest">Live-auction commerce</span>
          </span>
        </div>
      </footer>
    </div>
  );
}

function CardChosenFew() {
  return (
    <div className="share-card bg-gradient-to-b from-zinc-950 via-[#0a1224] to-zinc-950 text-white">
      <header className="flex items-center justify-between">
        <BidoraLogo size={48} inverse />
        <span className="text-xs uppercase tracking-[0.25em] text-amber-300 font-bold">The chosen few</span>
      </header>

      <h1 className="mt-16 text-center text-7xl sm:text-9xl font-black tracking-tighter leading-[0.9]">
        <span className="block text-amber-300">Spotlight</span>
        <span className="block">Sellers.</span>
      </h1>
      <p className="mt-6 text-center text-xl text-zinc-300 max-w-2xl mx-auto">
        Hand-picked, verified, crowned. The top 1% of sellers go straight on the front door.
      </p>

      <div className="relative mt-12 flex items-end justify-center h-[720px]">
        <PhoneFrame src={SHOTS.live} scale={1.05} />
      </div>

      <footer className="mt-10 flex flex-col items-center gap-4">
        <TrustPills items={["Founder seller perks", "Gold-crown badge", "Top-of-feed always"]} />
        <span className="text-2xl font-black tracking-tight">bidorah.com</span>
      </footer>
    </div>
  );
}

function CardStorageUnit() {
  return (
    <div className="share-card bg-gradient-to-br from-amber-50 via-orange-50 to-white">
      <header className="flex items-center justify-between">
        <BidoraLogo size={48} />
        <span className="px-4 py-2 rounded-full bg-amber-400 text-zinc-950 text-xs font-black uppercase tracking-wider">Trending category</span>
      </header>

      <h1 className="mt-12 text-7xl sm:text-8xl font-black tracking-tighter leading-[0.9]">
        Storage <span className="text-amber-600">unit</span> finds.
      </h1>
      <p className="mt-6 text-xl text-zinc-700 max-w-2xl">
        Live-auction the unknown. From abandoned units to estate clear-outs — bid blind, win bold.
      </p>

      <div className="relative mt-10 h-[700px] flex items-end justify-center">
        <PhoneFrame src={SHOTS.categories} scale={1.0} tilt={-3} />
        <PhoneFrame src={SHOTS.live}       scale={0.9} tilt={8} className="absolute right-[10%] bottom-0" />
      </div>

      <footer className="mt-10 flex flex-col items-center gap-4">
        <TrustPills items={["Bid live", "Win in seconds", "Ship same week"]} />
        <span className="text-2xl font-black tracking-tight">bidorah.com</span>
      </footer>
    </div>
  );
}

function CardRealtimeBids() {
  return (
    <div className="share-card bg-gradient-to-tr from-[#0F172A] via-[#1D78AB] to-cyan-400 text-white">
      <header className="flex items-center justify-between">
        <BidoraLogo size={48} inverse />
        <span className="px-4 py-2 rounded-full bg-white text-zinc-950 text-xs font-black flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE NOW
        </span>
      </header>

      <h1 className="mt-12 text-7xl sm:text-8xl font-black tracking-tighter leading-[0.9]">
        Real-time<br/><span className="text-yellow-300">bids.</span>
      </h1>
      <p className="mt-6 text-xl text-blue-50/90 max-w-2xl">
        No mailbox bidding. No 48-hour holds. Tap, win, ship. Pure adrenaline commerce.
      </p>

      <div className="relative mt-12 h-[700px] flex items-center justify-center">
        <PhoneFrame src={SHOTS.live} scale={1.1} />
      </div>

      <footer className="mt-8 flex flex-col items-center gap-4">
        <TrustPills items={["Stripe + PayPal", "Refundable", "Verified sellers", "U.S. shipping"]} />
        <span className="text-2xl font-black tracking-tight">bidorah.com</span>
      </footer>
    </div>
  );
}

function CardWholesale() {
  return (
    <div className="share-card bg-gradient-to-b from-emerald-50 via-white to-emerald-50">
      <header className="flex items-center justify-between">
        <BidoraLogo size={48} />
        <span className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-black uppercase tracking-wider">B2B-gated</span>
      </header>

      <h1 className="mt-12 text-7xl sm:text-8xl font-black tracking-tighter leading-[0.9]">
        Wholesale,<br/><span className="text-emerald-700">gated.</span>
      </h1>
      <p className="mt-6 text-xl text-zinc-700 max-w-2xl">
        TIN. Reseller Permit. Business entity. Verified by admin. Only real buyers get past the velvet rope.
      </p>

      <div className="relative mt-10 h-[700px] flex items-end justify-center">
        <PhoneFrame src={SHOTS.categories} scale={1.05} />
      </div>

      <footer className="mt-10 flex flex-col items-center gap-4">
        <TrustPills items={["Reseller permit verified", "TIN required", "Admin-approved", "Bulk lots"]} />
        <span className="text-2xl font-black tracking-tight">bidorah.com</span>
      </footer>
    </div>
  );
}

const VARIANTS = {
  "bid-live":       { component: CardBidLive,       title: "Bid live. Win real." },
  "chosen-few":     { component: CardChosenFew,     title: "Spotlight sellers." },
  "storage-unit":   { component: CardStorageUnit,   title: "Storage unit finds." },
  "realtime-bids":  { component: CardRealtimeBids,  title: "Real-time bids." },
  "wholesale":      { component: CardWholesale,     title: "Wholesale, gated." },
};

export default function ShareCards() {
  const { slug } = useParams();
  const variant = VARIANTS[slug] || VARIANTS["bid-live"];
  const Card = variant.component;

  return (
    <div className="share-cards-page">
      {/* Variant picker — hidden in screenshots via :not(:hover) trick? No,
          just sits above the card. Easy to crop out when capturing. */}
      <div className="share-cards-picker">
        <span className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Variant:</span>
        {Object.entries(VARIANTS).map(([key, v]) => (
          <a
            key={key}
            href={`/share-cards/${key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
              key === (slug || "bid-live")
                ? "bg-zinc-950 text-white"
                : "bg-white text-zinc-700 ring-1 ring-zinc-300 hover:bg-zinc-100"
            }`}
          >
            {v.title}
          </a>
        ))}
        <span className="ml-auto text-xs text-zinc-500">
          Screenshot the card below at <strong>1080×1350</strong> for IG / TikTok cover.
        </span>
      </div>

      <div data-card>
        <Card />
      </div>
    </div>
  );
}
